import { gql, useQuery } from "@apollo/client";
import { Button, Checkbox, Tag, Text, useToastContext } from "@ttc3k/trekker";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import {
  MembershipBySubjectAndOrganizationDocument,
  type MembershipBySubjectAndOrganizationQuery,
  useMembershipMarkRenewedMutation,
  useMembershipSetManualRenewalDateMutation,
  useMembershipSuggestedRenewalDateQuery,
} from "gql/generated";
import { WhereaboutsModal } from "shared/components";
import { useNomenclature } from "shared/hooks";
import { formatRenewalDate } from "shared/utils";
import { Flex } from "styled-system/jsx";
import { EditPencil } from "iconoir-react";
import { EditRenewalDateModal } from "./EditRenewalDateModal";
import {
  formatInvoiceAmount,
  invoiceHref,
  renewalDayOnOrAfter,
} from "./renewalDateUtils";

const MEMBERSHIP_PAID_UP_SOURCE = gql`
  query MembershipPaidUpSource(
    $subjectType: String!
    $subjectEntityId: MongoID!
    $organizationId: MongoID!
  ) {
    membershipPaidUpSource(
      subjectType: $subjectType
      subjectEntityId: $subjectEntityId
      organizationId: $organizationId
    ) {
      storefrontId
      orderId
      renewablePurchaseId
      paidUntil
    }
  }
`;

const DAY_MS = 24 * 60 * 60 * 1000;
const DEFAULT_REMINDER_LEAD_DAYS = 30;

type RenewalDateLineProps = {
  membership:
    | NonNullable<
        MembershipBySubjectAndOrganizationQuery["membershipBySubjectAndOrganization"]
      >
    | null
    | undefined;
  renewalSettings?: {
    enabled?: boolean | null;
    term?: string | null;
    termYears?: number | null;
    renewalType?: string | null;
    fixedRenewalMonth?: number | null;
    fixedRenewalDay?: number | null;
    renewalReminderLeadDays?: number | null;
    pendingStatusTagId?: string | null;
    activeStatusTagId?: string | null;
    expiredStatusTagId?: string | null;
  } | null;
  operatorEntityId?: string;
  organizationId?: string;
  readOnly?: boolean;
  /** Current membership status tag ids (from the tag editor's live selection,
   * not the possibly-stale `membership.statusTagIds`), used to offer syncing
   * status tags alongside "Mark Renewed". */
  currentStatusTagIds?: string[];
  /** Applies a full status-tag-id replacement (parent merges it into the
   * operator's complete tag list and may offer a renewal email prompt). */
  onApplyStatusTagSync?: (nextStatusIds: string[]) => void | Promise<void>;
  /**
   * Apollo app-wide default is `no-cache`, so mutation `refetchQueries` alone
   * often won't refresh this badge — call the parent's active query refetch.
   */
  onMembershipUpdated?: () => void | Promise<unknown>;
  /**
   * When set to "listing", attribution shows via listing (People app showing a
   * listing-owned OPERATOR membership). Overrides admin/purchase labels.
   */
  attributionSource?: "listing";
  listingName?: string | null;
};

const parseDate = (raw?: string | null): Date | null => {
  if (!raw) return null;
  const d = new Date(raw);
  return isNaN(d.getTime()) ? null : d;
};

export const RenewalDateLine = ({
  membership,
  renewalSettings,
  operatorEntityId,
  organizationId,
  readOnly = false,
  currentStatusTagIds = [],
  onApplyStatusTagSync,
  onMembershipUpdated,
  attributionSource,
  listingName,
}: RenewalDateLineProps) => {
  const { t } = useTranslation();
  const { toastFactory } = useToastContext();
  const { member } = useNomenclature();
  const [editOpen, setEditOpen] = useState(false);
  const [confirmRenewal, setConfirmRenewal] = useState(false);
  const [alsoUpdateStatusTags, setAlsoUpdateStatusTags] = useState(true);

  const canEdit = !readOnly && !!operatorEntityId && !!organizationId;
  const dateDriven =
    !!membership &&
    !!renewalSettings?.enabled &&
    renewalSettings.term !== "MANUAL" &&
    renewalSettings.term !== "LIFETIME";

  const [setManualDate, { loading: savingDate }] =
    useMembershipSetManualRenewalDateMutation();
  const [markRenewed, { loading: markingRenewed }] =
    useMembershipMarkRenewedMutation();
  const { data: suggestionData, refetch: refetchSuggestion } =
    useMembershipSuggestedRenewalDateQuery({
      variables: {
        subjectType: "OPERATOR",
        subjectEntityId: operatorEntityId ?? "",
        organizationId: organizationId ?? "",
      },
      skip: !canEdit || !dateDriven,
      fetchPolicy: "network-only",
    });
  const suggestion = suggestionData?.membershipSuggestedRenewalDate;
  const openInvoice = suggestion?.blockedByInvoice ?? null;

  const { data: paidUpSourceData } = useQuery<{
    membershipPaidUpSource: {
      storefrontId: string;
      orderId: string;
      renewablePurchaseId: string;
      paidUntil?: string | null;
    } | null;
  }>(MEMBERSHIP_PAID_UP_SOURCE, {
    variables: {
      subjectType: "OPERATOR",
      subjectEntityId: operatorEntityId,
      organizationId,
    },
    skip: !operatorEntityId || !organizationId || !dateDriven,
  });
  const paidUpSource = paidUpSourceData?.membershipPaidUpSource;
  const orderHref =
    paidUpSource && organizationId
      ? `/app/organization/${organizationId}/storefront/${paidUpSource.storefrontId}/order/${paidUpSource.orderId}`
      : null;

  if (!membership || !renewalSettings?.enabled) return null;
  if (renewalSettings.term === "MANUAL") return null;

  if (renewalSettings.term === "LIFETIME") {
    return (
      <Text size="xs" color="text.mid">
        {t("directory:OPERATOR.BACK_OFFICE.RENEWAL_DATE.LIFETIME")}
      </Text>
    );
  }

  const fixedDate =
    renewalSettings.renewalType === "FIXED_DATE" &&
    renewalSettings.fixedRenewalMonth &&
    renewalSettings.fixedRenewalDay
      ? {
          month: renewalSettings.fixedRenewalMonth,
          day: renewalSettings.fixedRenewalDay,
        }
      : null;

  // The stored date is the renewal day (fixed-date orgs round a day-before
  // value up). Anniversary orgs show the stored date as-is: their server-side
  // renewal calculation is fixed separately.
  const manualDate = parseDate(membership.manualRenewalDate);
  const storedDate = manualDate ?? parseDate(membership.currentPeriodEnd);
  const hasManualDate = !!manualDate;
  const renewalDate = storedDate
    ? fixedDate
      ? renewalDayOnOrAfter(storedDate, fixedDate.month, fixedDate.day)
      : storedDate
    : null;
  const formattedRenewalDate = renewalDate
    ? formatRenewalDate(renewalDate, "MMM d, yyyy")
    : null;

  // Phase comes from the server (getRenewalState) so styling matches the
  // renewal sweep, reminders and lists.
  const phase =
    membership.renewalState?.phase ?? (renewalDate ? "PAID_UP" : "UNKNOWN");

  const paidUntil = paidUpSource?.paidUntil
    ? new Date(`${paidUpSource.paidUntil}T00:00:00.000Z`)
    : null;
  const formattedPaidThrough =
    paidUntil && !isNaN(paidUntil.getTime())
      ? formatRenewalDate(new Date(paidUntil.getTime() - DAY_MS), "MMM d, yyyy")
      : null;

  const pendingTagId = renewalSettings.pendingStatusTagId
    ? String(renewalSettings.pendingStatusTagId)
    : undefined;
  const activeTagId = renewalSettings.activeStatusTagId
    ? String(renewalSettings.activeStatusTagId)
    : undefined;
  const expiredTagId = renewalSettings.expiredStatusTagId
    ? String(renewalSettings.expiredStatusTagId)
    : undefined;
  const hasPendingTagNow =
    !!pendingTagId && currentStatusTagIds.includes(pendingTagId);
  const hasActiveTagNow =
    !!activeTagId && currentStatusTagIds.includes(activeTagId);
  const canOfferStatusSync =
    !!onApplyStatusTagSync &&
    (!!pendingTagId || !!activeTagId) &&
    (hasPendingTagNow || !hasActiveTagNow);

  const applyActiveStatusTags = () => {
    const nextStatusIds = currentStatusTagIds
      .filter(id => id !== pendingTagId && id !== expiredTagId)
      .concat(
        activeTagId && !currentStatusTagIds.includes(activeTagId)
          ? [activeTagId]
          : [],
      );
    void onApplyStatusTagSync?.(nextStatusIds);
  };

  const refetchQueries =
    operatorEntityId && organizationId
      ? [
          {
            query: MembershipBySubjectAndOrganizationDocument,
            variables: {
              subjectType: "OPERATOR",
              subjectEntityId: operatorEntityId,
              organizationId,
            },
          },
        ]
      : [];

  const afterWrite = async () => {
    await onMembershipUpdated?.();
    await refetchSuggestion();
  };

  const handleSaveDate = async ({
    date,
    note,
    syncStatusTags,
  }: {
    date: Date;
    note: string;
    syncStatusTags: boolean;
  }) => {
    if (!operatorEntityId || !organizationId) return;
    try {
      await setManualDate({
        variables: {
          operatorEntityId,
          organizationId,
          date: date.toISOString(),
          note: note.trim() || undefined,
        },
        refetchQueries,
        awaitRefetchQueries: true,
      });
      if (syncStatusTags) applyActiveStatusTags();
      await afterWrite();
      toastFactory.create({
        title: t("directory:OPERATOR.BACK_OFFICE.RENEWAL_DATE.EDIT_SUCCESS"),
      });
      setEditOpen(false);
    } catch {
      toastFactory.create({
        title: t("directory:OPERATOR.BACK_OFFICE.RENEWAL_DATE.EDIT_ERROR"),
      });
    }
  };

  const handleRemoveDate = async () => {
    if (!operatorEntityId || !organizationId) return;
    try {
      await setManualDate({
        variables: { operatorEntityId, organizationId, date: null },
        refetchQueries,
        awaitRefetchQueries: true,
      });
      await afterWrite();
      toastFactory.create({
        title: t("directory:OPERATOR.BACK_OFFICE.RENEWAL_DATE.REMOVE_SUCCESS"),
      });
      setEditOpen(false);
    } catch {
      toastFactory.create({
        title: t("directory:OPERATOR.BACK_OFFICE.RENEWAL_DATE.EDIT_ERROR"),
      });
    }
  };

  const handleConfirmMarkRenewed = async () => {
    if (!operatorEntityId || !organizationId) return;
    const shouldSyncStatusTags = canOfferStatusSync && alsoUpdateStatusTags;
    try {
      await markRenewed({
        variables: { operatorEntityId, organizationId },
        refetchQueries,
        awaitRefetchQueries: true,
      });
      if (shouldSyncStatusTags) applyActiveStatusTags();
      await afterWrite();
      toastFactory.create({
        title: t(
          "directory:OPERATOR.BACK_OFFICE.RENEWAL_DATE.MARK_RENEWED_SUCCESS",
        ),
      });
      setConfirmRenewal(false);
    } catch (err) {
      toastFactory.create({
        title: t("directory:OPERATOR.BACK_OFFICE.RENEWAL_DATE.EDIT_ERROR"),
        description: err instanceof Error ? err.message : undefined,
      });
    }
  };

  const suggestedDate = parseDate(suggestion?.suggestedDate);
  const periodStart = parseDate(suggestion?.periodStart);
  const periodEnd = parseDate(suggestion?.periodEnd);
  const openInvoiceLink =
    openInvoice && organizationId
      ? invoiceHref(organizationId, openInvoice)
      : null;

  return (
    <Flex flexDir="column" gap="100">
      <Flex alignItems="flex-start" gap="150" flexWrap="wrap">
        {!formattedRenewalDate ? (
          <Flex flexDir="column" gap="25">
            <Tag size="sm" visual="warning" mode="light">
              {t("directory:OPERATOR.BACK_OFFICE.RENEWAL_DATE.NOT_SET_WARNING")}
            </Tag>
            {canEdit && (
              <Text visual="smallRegular" color="text.mid">
                {t("directory:OPERATOR.BACK_OFFICE.RENEWAL_DATE.NOT_SET_HINT")}
              </Text>
            )}
          </Flex>
        ) : (
          <Flex flexDir="column" gap="25">
            {phase === "EXPIRED" ? (
              <Tag size="sm" visual="destructive" mode="light">
                {t("directory:OPERATOR.BACK_OFFICE.RENEWAL_DATE.EXPIRED", {
                  date: formattedRenewalDate,
                })}
              </Tag>
            ) : phase === "RENEWAL_WINDOW" ? (
              <Tag size="sm" visual="highlight" mode="light">
                {t(
                  "directory:OPERATOR.BACK_OFFICE.RENEWAL_DATE.RENEWAL_PENDING",
                  {
                    date: formattedRenewalDate,
                  },
                )}
              </Tag>
            ) : (
              <Tag size="sm" visual="success" mode="light">
                {t("directory:OPERATOR.BACK_OFFICE.RENEWAL_DATE.RENEWS_ON", {
                  date: formattedRenewalDate,
                })}
              </Tag>
            )}
            <Text visual="smallRegular" color="text.mid">
              {attributionSource === "listing"
                ? listingName?.trim()
                  ? t(
                      "directory:OPERATOR.BACK_OFFICE.RENEWAL_DATE.VIA_LISTING_NAMED",
                      {
                        listingName: listingName.trim(),
                      },
                    )
                  : t("directory:OPERATOR.BACK_OFFICE.RENEWAL_DATE.VIA_LISTING")
                : hasManualDate
                  ? t("directory:OPERATOR.BACK_OFFICE.RENEWAL_DATE.VIA_ADMIN")
                  : t(
                      "directory:OPERATOR.BACK_OFFICE.RENEWAL_DATE.VIA_PURCHASE",
                    )}
            </Text>
          </Flex>
        )}
        {canEdit && (
          <>
            <Button
              type="button"
              Icon={EditPencil}
              mode="light"
              size="sm"
              h="24px"
              minW="24px"
              w="24px"
              p="0"
              aria-label={t(
                "directory:OPERATOR.BACK_OFFICE.RENEWAL_DATE.EDIT_TITLE",
              )}
              onClick={() => setEditOpen(true)}
            />
            <Button
              type="button"
              mode={formattedRenewalDate ? "light" : "dark"}
              visual={formattedRenewalDate ? undefined : "primary"}
              size="sm"
              h="24px"
              disabled={!!openInvoice || !suggestedDate}
              onClick={() => {
                setAlsoUpdateStatusTags(true);
                setConfirmRenewal(true);
              }}
            >
              {t("directory:OPERATOR.BACK_OFFICE.RENEWAL_DATE.MARK_RENEWED")}
            </Button>
          </>
        )}
      </Flex>

      {formattedPaidThrough && (
        <Flex alignItems="center" gap="100">
          <Text visual="smallRegular" color="text.mid">
            {t(
              "directory:OPERATOR.BACK_OFFICE.RENEWAL_DATE.STOREFRONT_PAID_THROUGH",
              {
                date: formattedPaidThrough,
              },
            )}
          </Text>
          {orderHref && (
            <Link to={orderHref} style={{ textDecoration: "none" }}>
              <Text visual="smallRegular" color="text.accent.blue.mid">
                {t("directory:OPERATOR.BACK_OFFICE.RENEWAL_DATE.VIEW_ORDER")}
              </Text>
            </Link>
          )}
        </Flex>
      )}

      {canEdit && openInvoice && (
        <Flex alignItems="center" gap="100" flexWrap="wrap">
          <Text visual="smallRegular" color="text.mid">
            {t(
              "directory:OPERATOR.BACK_OFFICE.RENEWAL_DATE.BLOCKED_BY_INVOICE",
              {
                amount: formatInvoiceAmount(openInvoice),
                date: openInvoice.dueDate
                  ? formatRenewalDate(openInvoice.dueDate, "MMM d, yyyy")
                  : "",
              },
            )}
          </Text>
          {openInvoiceLink && (
            <Link to={openInvoiceLink} style={{ textDecoration: "none" }}>
              <Text visual="smallRegular" color="text.accent.blue.mid">
                {t("directory:OPERATOR.BACK_OFFICE.RENEWAL_DATE.OPEN_INVOICE")}
              </Text>
            </Link>
          )}
        </Flex>
      )}

      {confirmRenewal && suggestedDate && (
        <WhereaboutsModal
          isOpen
          onClose={() => setConfirmRenewal(false)}
          title={t(
            "directory:OPERATOR.BACK_OFFICE.RENEWAL_DATE.MARK_RENEWED_TITLE",
          )}
          size="sm"
          isMaxZIndex
          saveButtonCopy={t(
            "directory:OPERATOR.BACK_OFFICE.RENEWAL_DATE.MARK_RENEWED_CONFIRM",
          )}
          saveButtonProps={{
            disabled: markingRenewed,
            isLoading: markingRenewed,
            onClick: () => void handleConfirmMarkRenewed(),
          }}
          cancelButtonProps={{ onClick: () => setConfirmRenewal(false) }}
        >
          <Flex flexDir="column" gap="200">
            <Text color="text.mid">
              {renewalDate && renewalDate.getTime() > Date.now()
                ? t(
                    "directory:OPERATOR.BACK_OFFICE.RENEWAL_DATE.MARK_RENEWED_EARLY",
                    {
                      member: member.toLowerCase(),
                      coveredThrough: formatRenewalDate(
                        new Date(renewalDate.getTime() - DAY_MS),
                        "MMM d, yyyy",
                      ),
                      date: formatRenewalDate(suggestedDate, "MMMM d, yyyy"),
                    },
                  )
                : periodStart && periodEnd
                  ? t(
                      "directory:OPERATOR.BACK_OFFICE.RENEWAL_DATE.MARK_RENEWED_MESSAGE",
                      {
                        start: formatRenewalDate(periodStart, "MMM d, yyyy"),
                        end: formatRenewalDate(periodEnd, "MMM d, yyyy"),
                        date: formatRenewalDate(suggestedDate, "MMM d, yyyy"),
                      },
                    )
                  : t(
                      "directory:OPERATOR.BACK_OFFICE.RENEWAL_DATE.MARK_RENEWED_NEXT_ONLY",
                      {
                        date: formatRenewalDate(suggestedDate, "MMM d, yyyy"),
                      },
                    )}
            </Text>
            {canOfferStatusSync && (
              <Checkbox
                label={t(
                  "directory:OPERATOR.BACK_OFFICE.RENEWAL_DATE.MARK_RENEWED_ALSO_TAG_ACTIVE",
                )}
                checked={alsoUpdateStatusTags}
                onCheckedChange={({ checked }) =>
                  setAlsoUpdateStatusTags(checked === true)
                }
              />
            )}
          </Flex>
        </WhereaboutsModal>
      )}

      {canEdit && organizationId && (
        <EditRenewalDateModal
          isOpen={editOpen}
          onClose={() => setEditOpen(false)}
          organizationId={organizationId}
          currentRenewalDate={renewalDate}
          fixedDate={fixedDate}
          reminderLeadDays={
            renewalSettings.renewalReminderLeadDays ??
            DEFAULT_REMINDER_LEAD_DAYS
          }
          openInvoice={openInvoice}
          canOfferStatusSync={canOfferStatusSync}
          saving={savingDate}
          onSave={handleSaveDate}
          onRemove={handleRemoveDate}
        />
      )}
    </Flex>
  );
};
